import apiClient from "@/api/apiClient";
import {
  type CurrentUserProfile,
  type GetDeploymentAdminsProps,
  type PaginatedDeploymentUser,
  type PaginatedWorkspaceUser,
} from "@/api/resources/users/types";
import { DeploymentRole, WorkspaceRole, type UserProfile } from "@/supabase.types";
import { type ApiResourceConfig } from "@/utils/api";

const table = "user_profiles";
const queryUser = `
                    *,
                    users_to_deployments(
                      *,
                      deployment:deployments!inner(*)
                    ),
                    users_to_workspaces(
                      *,
                      workspace:workspaces!inner(*)
                    )
                  `;
const queryUserWorkspace = "*, users_to_workspaces!inner(*), users_to_deployments(*)";
const queryUserDeployment =
  "*, users_to_deployments!inner(*, deployment:deployments!inner(id, workspace:workspaces!inner(id, creator_workspace_id))), users_to_workspaces(*)";
export const apiConfigUsers: ApiResourceConfig<typeof table, typeof queryUser> = {
  table,
  searchables: ["first_name", "last_name", "email", "phone"],
  sortables: ["first_name", "last_name", "email", "created_at", "updated_at"],
};

export const getUser = async (match: Partial<UserProfile>): Promise<CurrentUserProfile | null> => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryUser)
    .match(match)
    .is("users_to_deployments.deployment.deleted_at", null)
    .is("users_to_workspaces.workspace.deleted_at", null)
    .maybeSingle();
  if (error) throw error;
  return data;
};

export const getWorkspaceUsers = async (workspace_id: string): Promise<PaginatedWorkspaceUser[]> => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryUserWorkspace)
    .eq("users_to_workspaces.workspace_id", workspace_id);
  if (error) throw error;
  return data;
};

export const getWorkspaceAdmins = async (workspace_id: string): Promise<PaginatedWorkspaceUser[]> => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryUserWorkspace)
    .eq("users_to_workspaces.workspace_id", workspace_id)
    .eq("users_to_workspaces.role", WorkspaceRole.admin);
  if (error) throw error;
  return data;
};

export const getDeploymentUsers = async (deployment_id: string): Promise<PaginatedDeploymentUser[]> => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryUserDeployment)
    .eq("users_to_deployments.deployment_id", deployment_id);
  if (error) throw error;
  return data;
};

export const getDeploymentsUsers = async (deployment_ids: string[]): Promise<PaginatedDeploymentUser[]> => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryUserDeployment)
    .in("users_to_deployments.deployment_id", deployment_ids);
  if (error) throw error;
  return data;
};

export const getDeploymentAdmins = async ({
  filteredDeployments,
  globalFilteredDeployments,
}: GetDeploymentAdminsProps): Promise<PaginatedDeploymentUser[]> => {
  const q = apiClient
    .from(table)
    .select(queryUserDeployment)
    .in("users_to_deployments.deployment_id", filteredDeployments)
    .eq("users_to_deployments.role", DeploymentRole.admin);

  if (globalFilteredDeployments.length > 0) {
    q.in("users_to_deployments.deployment_id", globalFilteredDeployments);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data;
};
